import React, { useMemo } from 'react';
import { Switch } from '@mui/material';
import { useInvoiceSearchMetaDataByColumn } from 'src/use-cases/invoice/useInvoices';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { getCurrentFilterValues } from 'src/presentations/components/molecules/table/columnHeader';
import {
  ColumnHeaderProvider,
  ColumnHeader,
  calculateFilterCount,
  useAutoSelectFilter,
  useBadgeFilter,
  TableFilterItem,
} from 'src/presentations/components/molecules/table/columnHeader';
import { useAuditDashboardFilter } from './hooks/useAuditDashboardFilter';

interface AuditColumnHeaderProps {
  column: any;
  accessorKey: string;
  customHeader?: React.ReactNode;
  filters: any;
  keyword: string;
  dateReference: string;
  onSort: (method: 'asc' | 'desc', field: string) => void;
  onTableFilter: (accessorKey: string, values: string[]) => void;
}

// Helper function to map UI column keys to API column enum
const mapAccessorKeyToApiColumn = (accessorKey: string): string | undefined => {
  const mapping: Record<string, string> = {
    invoiceNumber: 'INVOICE_NUMBER',
    status: 'STATUS',
    billedEntity: 'BILLED_ENTITY',
    incoterm: 'INCOTERM',
    shipper: 'SHIPPER',
    consignee: 'CONSIGNEE',
    service: 'SERVICE',
    shipmentType: 'SHIPMENT_TYPE',
    tradeLane: 'TRADE_LANE',
    shipmentNumber: 'SHIPMENT_NUMBER',
    date: 'DATE',
  };

  // Return undefined for sort-only columns (no backend metadata support)
  if (accessorKey === 'totalAmount' || accessorKey === 'deviationAmount') {
    return undefined;
  }

  return mapping[accessorKey];
};

/**
 * Custom Column Header Component for Audit Dashboard
 *
 * Features:
 * - Auto-select all items on first load
 * - SPOT filtering for shipment numbers
 * - Smart counter badge (shows only when partial selection or SPOT active)
 * - Lazy loading of filter metadata
 */
export const AuditColumnHeader: React.FC<AuditColumnHeaderProps> = ({
  column,
  accessorKey,
  customHeader,
  filters,
  keyword,
  dateReference,
  onSort,
  onTableFilter,
}) => {
  const columnKey = mapAccessorKeyToApiColumn(accessorKey);

  // Memoize currentFilterValues based on actual filter content, not object reference
  // The filters object is recreated on every Dashboard context update, but the actual
  // filter values may remain the same. Using JSON.stringify for deep comparison.
  const currentFilterValues = useMemo(() => {
    const values = getCurrentFilterValues(accessorKey, filters);
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessorKey, JSON.stringify(filters?.filters)]);

  // Fetch metadata immediately (no lazy loading for now - can be optimized later)
  const { data: columnMetadata, isLoading } = useInvoiceSearchMetaDataByColumn(
    columnKey || '',
    keyword || '',
    filters || {},
    !!columnKey // Fetch immediately if columnKey exists
  );

  // Transform API response to filter items
  const filterItems = useMemo<TableFilterItem[]>(() => {
    // For shipment number, use detailedCounts (includes SPOT badge data)
    if (columnKey === 'SHIPMENT_NUMBER') {
      if (!columnMetadata?.detailedCounts) return [];
      // Map detailedCounts to TableFilterItem with badge
      return (columnMetadata.detailedCounts as any[]).map((item) => ({
        id: item.value || '',
        label: item.value || '',
        count: item.count || 0,
        badge: item.hasSpotShipment
          ? {
              label: 'SPOT',
              bgColor: '#F2F4F7',
              textColor: '#737373',
            }
          : undefined,
      }));
    }
    // For other columns, use simple counts
    if (!columnMetadata?.counts) return [];
    return createFilterItems(columnMetadata.counts, accessorKey);
  }, [columnMetadata, columnKey, accessorKey]);

  // Auto-select behavior (select all on first load, auto-select new items on scroll)
  // Auto-select behavior (select all on first load, auto-select new items on scroll)
  // customized hook connected to AuditFilterContext
  const { selectedValues, setSelectedValues } = useAuditDashboardFilter({
    accessorKey,
    filterItems,
    initialSelectedValues: currentFilterValues || [],
    enabled: true,
  });

  // SPOT filter (only for shipment number column)
  const {
    onlyShowBadge,
    setOnlyShowBadge,
    handleApply: handleBadgeApply,
    wasApplied,
    badgeLabel,
  } = useBadgeFilter({
    column,
    filterItems,
    selectedValues,
    setSelectedValues,
    badgeLabel: 'SPOT',
    filterLabel: 'Only Show SPOT',
  });

  // Calculate counter badge (show applied count if partial selection or SPOT filter active)
  const count = calculateFilterCount(filterItems, currentFilterValues || [], wasApplied);

  //Filter visible items based on SPOT toggle (only for display, doesn't affect selection)
  const visibleFilterItems = useMemo(() => {
    if (columnKey === 'SHIPMENT_NUMBER' && onlyShowBadge) {
      // Show only items with SPOT badge when toggle is active
      return filterItems.filter((item) => item.badge?.label === badgeLabel);
    }
    return filterItems;
  }, [columnKey, onlyShowBadge, filterItems, badgeLabel]);

  // Custom filter slot for SPOT toggle (only show for shipment number)
  const customFilterSlot =
    columnKey === 'SHIPMENT_NUMBER' ? (
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-8'>
          <span className='text-12 text-blue_grey-90'>Only Show</span>
          <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>
        </div>
        <Switch
          checked={onlyShowBadge}
          onChange={(e) => setOnlyShowBadge(e.target.checked)}
          sx={{
            width: 36,
            height: 20,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: '2px',
              transitionDuration: '300ms',
              color: '#fff',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#00992B',
                  opacity: 1,
                  border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.5,
                },
              },
              '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 16,
              height: 16,
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.10)',
            },
            '& .MuiSwitch-track': {
              borderRadius: 12,
              backgroundColor: '#F2F4F7',
              opacity: 1,
              transition: 'background-color 500ms',
            },
          }}
        />
      </div>
    ) : undefined;

  return (
    <ColumnHeaderProvider
      column={column}
      sort={{
        ascText: 'Sort A to Z',
        descText: 'Sort Z to A',
        onSort: (_columnId: string, direction: 'asc' | 'desc') => {
          const method = direction === 'desc' ? 'desc' : 'asc';
          const field = accessorKey === 'date' ? dateReference : accessorKey;
          onSort(method, field);
        },
      }}
      items={{
        items: visibleFilterItems,
        isLoading: columnKey ? isLoading : false,
        count,
      }}
      filter={{
        initialSelectedValues: selectedValues,
        onFilter: (_columnId: string, values: string[]) => {
          // If SPOT filter and shipment column, use badge filter handler
          if (columnKey === 'SHIPMENT_NUMBER' && onlyShowBadge) {
            handleBadgeApply((id, filteredValues) => {
              setSelectedValues(filteredValues);
              onTableFilter(accessorKey, filteredValues);
            });
          } else {
            setSelectedValues(values);
            onTableFilter(accessorKey, values);
          }
        },
      }}
      customFilterSlot={customFilterSlot}
    >
      <ColumnHeader customHeader={customHeader} />
    </ColumnHeaderProvider>
  );
};
